import axios from 'axios'
import { getDeviceId, getDeviceSerial } from '../../utils/deviceData'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { store } from '../../stores'
import { getSummitMode } from '../../VNMode/Selectors'
import { ToastManager } from '../../utils/toastManager'
import { parseRubyHash } from '../../utils/formatters'
import { callPeripheralBridge } from '../../VNPeripheralBridge/Utils'
import { PERIPHERAL_BRIDGE_CALL_METHODS } from '../../VNPeripheralBridge/Enums'

// Portico registration related errors
const UNREGISTERED_DEVICE_ERRORS = [
  'Validation failed',
  'This device is locked',
  'is not configured for device access',
  'This device has no access',
  'You must provide a valid application key',
]

const VNLoginApi = () => {
  const porticoUrl = process.env.REACT_APP_OIDC_ISSUER
  const appKey = process.env.REACT_APP_OIDC_CLIENT_ID || null
  const organizationName = process.env.REACT_APP_ORG || null

  /**
   * Parses error message from server response from snake_case to friendly format
   * @param {Error} error
   * @returns
   */
  const parseErrorMessage = (error) => {
    const response = camelizeKeys(error?.response?.data)
    const friendlyMessage = response?.friendlyMessage
    const applicationMessage = response?.errorMessage
    const errorMessage = friendlyMessage ?? applicationMessage

    if (!friendlyMessage && errorMessage?.includes('"=>{')) {
      const parsed = parseRubyHash(errorMessage)
      return parsed.error_message || errorMessage
    }

    return errorMessage
  }

  /**
   * Interrupts login flow and redirects to device registration if device is locked
   * First clear invalid tokens from local storage
   * Then handle redirection (unless already on registration page)
   *
   * @param {String} error
   * @returns {Boolean} - whether the error was handled
   */
  const handleLockedDeviceError = (error = '', isSummit = false) => {
    const isUnregisteredDevice = UNREGISTERED_DEVICE_ERRORS.some((e) => error.includes(e))

    // should handle validation fail, remove tokens
    if (isUnregisteredDevice) {
      // reset access token and redirect to device registration
      localStorage.removeItem('accessToken')
      localStorage.removeItem('expiresAt')
    }

    // these are the two routes that device registration happens on
    // this should be re-worked so registration is not happening in between two routes
    const isAlreadyRegistering = window.location.href.includes('register-device') || window.location.href.includes('device-login')

    if (isAlreadyRegistering) {
      return false
    }

    if (isSummit) {
      callPeripheralBridge(PERIPHERAL_BRIDGE_CALL_METHODS.ON_SYNC_NETWORK_DATA)
    }

    if (isUnregisteredDevice) {
      window.location.href = '/register-device'
      return true
    }

    return false
  }

  /**
   * Post to the login endpoint to retrieve an access token
   * @returns {Promise} - Axios response Promise
   */
  const postDeviceLogin = async () => {
    if (!porticoUrl) {
      return Promise.reject(new Error('Missing base URL for Portico'))
    }
    if (!appKey || !organizationName) {
      return Promise.reject(new Error('Missing required parameters for device login'))
    }
    const deviceId = getDeviceId()
    const serialNumber = getDeviceSerial(false)
    const state = store.getState()
    const isSummit = getSummitMode(state)

    if (!deviceId && !serialNumber) {
      return Promise.reject(new Error('Missing device identifiers for device login'))
    }

    const data = { deviceId, appKey, organizationName }

    // in summit mode, use serial number instead of device ID
    if (isSummit) {
      data.serialNumber = serialNumber
      data.deviceId = undefined
    }

    try {
      const response = await axios.post(`${porticoUrl}/devices/login`, decamelizeKeys(data))
      return { data: camelizeKeys(response?.data) }
    } catch (error) {
      const errorMessage = parseErrorMessage(error)

      // handle locked device or validation error
      if (handleLockedDeviceError(errorMessage, isSummit)) return

      if (errorMessage) {
        ToastManager.error(`${errorMessage}`, { autoClose: true })
      }

      throw error
    }
  }
  return { postDeviceLogin }
}

export default VNLoginApi
